import {
  CollectionContext,
  GdprSccVendorReviewStatus,
  OrganizationDataRecipientDto,
} from "../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { OrganizationDto } from "../../../../common/service/server/dto/OrganizationDto";
import { getVendorClassificationSlug } from "./vendorDocumentationUtils";
import {
  doesGppSignalApplyForVendor,
  doesVendorHaveUsClassification,
  isCaContractFieldRequiredForVendor,
  isUsClassificationRequiredForVendor,
  isVendorDocumentationClassified,
  isVendorGdprClassificationComplete,
} from "./VendorDocumentationPage";
import { isEmployeeOnlyCollectionContext } from "../vendor-inventory/vendorInventoryUtils";

export type VendorTableCellPillDisplay = {
  text: string | "GREEN_CHECKMARK_ICON" | "GRAY_X_ICON";
  color?: "gray" | "yellow" | "green" | "blue"; // ignored if text is one of the icon literals
};

export const getVendorClassificationCell = (
  vendor: OrganizationDataRecipientDto,
  org: OrganizationDto,
  collectionContext: CollectionContext | CollectionContext[] = "CONSUMER",
): VendorTableCellPillDisplay => {
  if (!isUsClassificationRequiredForVendor(vendor, org, collectionContext)) {
    return {
      text: "-",
    };
  }

  if (!doesVendorHaveUsClassification(vendor)) {
    return {
      color: "yellow",
      text: "Unverified",
    };
  }

  return {
    color: "gray",
    text: {
      "service-provider": "Service Provider",
      contractor: "Contractor",
      "third-party": "Third Party",
    }[getVendorClassificationSlug(vendor)],
  };
};

export const getCaContractRequirementCell = (
  vendor: OrganizationDataRecipientDto,
): VendorTableCellPillDisplay => {
  if (!isCaContractFieldRequiredForVendor(vendor)) {
    return {
      text: "-",
    };
  }

  if (
    vendor.caContractRequirementUrl ||
    vendor.caContractRequirementFileKey ||
    vendor.cpraContractLanguageUrl
  ) {
    return {
      text: "GREEN_CHECKMARK_ICON",
    };
  } else {
    return {
      color: "yellow",
      text: "Unverified",
    };
  }
};

export const getGppSignalCell = (
  vendor: OrganizationDataRecipientDto,
): VendorTableCellPillDisplay => {
  if (!doesGppSignalApplyForVendor(vendor)) {
    return {
      text: "-",
    };
  }

  if (!vendor.gppSignalStatus) {
    return {
      color: "yellow",
      text: "Unverified",
    };
  }

  switch (vendor.gppSignalStatus) {
    case "GPP_SIGNAL_RESPECTED":
      return {
        text: "GREEN_CHECKMARK_ICON",
      };
    case "GPP_ALTERNATE_OPT_OUT_MECHANISM":
      return {
        text: "GREEN_CHECKMARK_ICON",
      };
    case "GPP_SIGNAL_NOT_RESPECTED_OR_UNKNOWN":
      return {
        color: "yellow",
        text: "Unverified",
      };
    default:
      throw new Error(`Unexpected GPP signal status: ${vendor.gppSignalStatus}`);
  }
};

export const getGdprClassificationCell = (
  vendor: OrganizationDataRecipientDto,
): VendorTableCellPillDisplay => {
  if (isEmployeeOnlyCollectionContext(vendor.collectionContext ?? [])) {
    return {
      text: "-",
    };
  }

  let isEeaUkRegion = vendor.processingRegions.includes("EEA_UK");

  if (!isEeaUkRegion) {
    return {
      text: "-",
    };
  }

  if (!isVendorGdprClassificationComplete(vendor)) {
    return {
      color: "yellow",
      text: "Unverified",
    };
  }

  if (vendor.gdprProcessorSetting == "Processor") {
    return {
      color: "gray",
      text: "Processor",
    };
  } else if (vendor.gdprProcessorSetting == "Controller") {
    return {
      color: "gray",
      text: "Controller",
    };
  } else {
    return {
      color: "gray",
      text: "Verified",
    };
  }
};

export const getDocumentationStatusCell = (
  org: OrganizationDto,
  vendor: OrganizationDataRecipientDto,
): VendorTableCellPillDisplay => {
  if (isVendorDocumentationClassified(org, vendor)) {
    return {
      color: "green",
      text: "Verified",
    };
  }

  return {
    color: "yellow",
    text: "Unverified",
  };
};

export const getInternationalDataTransferCellText = (
  status: GdprSccVendorReviewStatus,
): string | null => {
  switch (status) {
    case "DOCS_PROVIDED":
      return "SCC found";
    case "DPF_LIST":
      return "On the DPF list";
    case "FULFILLMENT_ONLY":
      return "Contract fulfillment use only";
    case "SAFE_COUNTRIES_ONLY":
      return "Safe country processing only";
    case "VENDOR_CONTACTED":
      return null; // return null since even though the vendor has been contacted, we still don't know the actual status
  }

  return null;
};
